import {NgForOf, NgIf} from "@angular/common";
import {Component, Inject, OnInit, ViewChild} from '@angular/core';
import {FlexModule} from "@angular/flex-layout";
import {FormBuilder, FormControl, FormGroup, ReactiveFormsModule, Validators} from "@angular/forms";
import {MatOption} from "@angular/material/autocomplete";
import {MatButton, MatIconButton} from "@angular/material/button";
import {MAT_DIALOG_DATA, MatDialog, MatDialogRef} from "@angular/material/dialog";
import {MatError, MatFormField} from "@angular/material/form-field";
import {MatIcon} from "@angular/material/icon";
import {MatInput} from "@angular/material/input";
import {MatProgressSpinner} from "@angular/material/progress-spinner";
import {MatSelect} from "@angular/material/select";
import {RetailerViewService} from "@features/leads/retailers/retailer-view/retailer-view.service";
import {OccupancyType} from "@features/leasing/leasing.model";
import {AccountType, BankOption, ValidateRequest} from "@features/settings/model/payments.model";
import {PaymentValidationService} from "@features/settings/service/payment-validation.service";
import {SnackbarService} from "@ui-kit/framework/util/service/snackbar.service";
import {lastValueFrom} from "rxjs";
import {AppSharedModule} from "../../../../../../core/modules/app-shared.module";
import {ConsoleContextService} from "../../../../../../security/console-context.service";
import {ErrorPopupComponent} from "../../../../../../shared/components/dialogs/error-popup/error-popup.component";
import {IconPopupComponent} from "../../../../../../shared/components/dialogs/icon-popup/icon-popup.component";

@Component({
  selector: 'app-retailer-banking-details-dialog',
  standalone: true,
  imports: [
    AppSharedModule,
    FlexModule,
    MatButton,
    MatError,
    MatFormField,
    MatInput,
    MatOption,
    MatSelect,
    NgForOf,
    NgIf,
    ReactiveFormsModule,
    MatIcon,
    MatIconButton,
    MatProgressSpinner
  ],
  templateUrl: './retailer-banking-details-dialog.component.html'
})
export class RetailerBankingDetailsDialogComponent implements OnInit {
  @ViewChild('ngOtpInput') ngOtpInputRef: any;
  otpMode: boolean = false;
  paymentMethodForm: FormGroup;

  otpForm: FormGroup;
  errMessage: string | null = '';
  loading: boolean = false;
  email: string = '';
  len = 0;
  retailerId: number;

  bankAccountTypes: AccountType[] = [];
  bankOptions: BankOption[] = [];
  paymentMethodsOpts =
    [
      {
        typeCode: "TRANSACT_DISTRICT_PAYOUT",
        typeName: "Transact district payout"
      }
    ];
  selectedPaymentMethod: string | null = null;

  constructor(
    public dialogRef: MatDialogRef<RetailerBankingDetailsDialogComponent>,
    @Inject(MAT_DIALOG_DATA) public data: any,
    private fb: FormBuilder,
    private context: ConsoleContextService,
    private dialogService: MatDialog,
    private snackBar: SnackbarService,
    private retailerViewService: RetailerViewService,
    private paymentValidationService: PaymentValidationService
  ) {
    this.paymentMethodForm = new FormGroup({
      accountName: new FormControl('', [Validators.required]),
      bankAccountType: new FormControl('', [Validators.required]),
      branchCode: new FormControl('', [Validators.required]),
      bankAccountNumber: new FormControl('', [Validators.required]),
      methodType: new FormControl('', [Validators.required]),
    })

    this.otpForm = this.resetForm();
    this.retailerId = data.retailerId;
  }

  ngOnInit(): void {
    this.getUserEmail();
    this.loadPaymentMethodData();
  }

  loadPaymentMethodData() {
    this.paymentValidationService.getBankListWithDefaultBranchCode()
      .then(res => this.bankOptions = res.sort((a, b) => a.bankName.localeCompare(b.bankName)));

    this.bankAccountTypes = this.getBankAccountTypes();

    this.retailerViewService.getPaymentMethodOptions(this.retailerId.toString())
      .subscribe({
        next: value => {
          this.paymentMethodsOpts = value;
          this.selectedPaymentMethod = value[0].typeCode;
          this.paymentMethodForm.get('methodType')?.setValue(this.selectedPaymentMethod);
        },
        error: err => {
          console.log(err);
          this.handleError(err);
        }
      })
  }

  validateBankAccountDetails() {
    this.loading = true;
    const request: ValidateRequest = {
      accountNumber: this.paymentMethodForm.get('bankAccountNumber')?.value,
      branchCode: this.paymentMethodForm.get('branchCode')?.value,
      accountType: this.paymentMethodForm.get('bankAccountType')?.value,
    }
    this.retailerViewService.validateRetailerBankingDetails(this.retailerId.toString(), request)
      .subscribe({
        next: value => {
          if (value === 'SUCCESS') {
            this.requestBankingDetailsUpdate();
          } else {
            this.loading = false;
            switch (value) {
              case 'INVALID_BRANCH_CODE':
                this.errMessage = 'Invalid bank name';
                break;
              case 'INVALID_ACCOUNT_NUMBER':
                this.errMessage = 'Invalid account number';
                break;
              case 'INVALID_ACCOUNT_TYPE':
                this.errMessage = 'Invalid account type';
                break;
              case 'ERROR_AUTH':
                this.errMessage = 'Unexpected error';
                break;
              case 'ERROR_WS':
                this.errMessage = 'Unexpected error';
                break;
              default:
                this.errMessage = 'Unexpected error';
                break;
            }
            this.snackBar.openSnackBar('Could not validate bank account details: ' + this.errMessage!);
          }
        },
        error: err => {
          console.log(err);
          this.loading = false;
          this.handleError(err);
        }
      })
  }

  requestBankingDetailsUpdate() {
    this.loading = true;
    this.retailerViewService.updateRetailerBankingDetailsRequestOTP(this.retailerId.toString())
      .subscribe({
        next: value => {
          this.loading = false;
          this.otpMode = true;
        },
        error: err => {
          console.log(err);
          this.loading = false;
          this.handleError(err);
        }
      })
  }

  verifyBankingDetailsOTP() {
    this.loading = true;
    if (this.otpForm.value.pin) {
      this.retailerViewService.updateRetailerBankingDetailsConfirm(this.retailerId.toString(), this.otpForm.get('pin')?.value, this.paymentMethodForm.value)
        .subscribe({
          next: value => {
            this.loading = false;
            this.close();
          },
          error: err => {
            console.log(err);
            this.loading = false;
            this.handleError(err);
          }
        });
    }
  }

  onOtpChange(event: any): void {
    this.otpForm.get('pin')!.setValue(event);
    this.getPinAmount();
    this.len = this.otpForm.value.pin.toString().length;
  }

  public getPinAmount(): boolean {
    return this.otpForm.value.pin ? this.otpForm.value.pin.toString().length < 4 : true;
  }

  close() {
    this.dialogRef.close();
  }

  public onResendOTP(): void {
    this.loading = true;
    this.errMessage = null;
    lastValueFrom(this.retailerViewService.updateRetailerBankingDetailsRequestOTP(this.retailerId.toString()))
      .then(() => {
        this.loading = false;
        // show success message
        this.dialogService.open(IconPopupComponent, {
          height: '300px',
          width: '350px',
          data: {
            description: 'We have sent a One Time Pin to',
            boldDescription: this.context.currentUser!.email,
          },
        });
      }, err => {
        this.handleError(err);
      });
  }

  private getUserEmail(): void {
    this.email = this.context.currentUser!.email;
  }

  private resetForm(): FormGroup {
    return this.fb.group({
      pin: [null, Validators.required],
    });
  }

  private handleError(err: any): void {
    switch (err.status) {
      case 400:
        if ('ValidationException' === err.headers.get('error_code')) {
          this.errMessage = '' + err.headers.get('error_message');
          if (this.errMessage.includes('400 : [ValidationException: ')) {
            let newMessage = this.errMessage.replace('400 : [ValidationException:', '');
            newMessage = newMessage.replace(']', '');
            this.errMessage = newMessage;
          }
          this.otpForm.get('pin')!.setValue(null);
          this.ngOtpInputRef.setValue(null);
        }
        break;
      default:
        this.dialogService.open(ErrorPopupComponent, {
          height: 'auto',
          width: 'auto',
          data: {
            description: 'an unexpected error: ' + err,
          },
        });
    }
  }

  getBankAccountTypes(): AccountType[] {
    return [
      {code: '1', type: 'Cheque'},
      {code: '2', type: 'Saving'}
    ];
  }

  protected readonly OccupancyType = OccupancyType;
}
